
import React from 'react';
import { Form } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { StatusField } from './fields/StatusField';
import { LearningStatusField } from './fields/LearningStatusField';
import { SubmissionField } from './fields/SubmissionField';
import { TeachingInfoFields } from './fields/TeachingInfoFields';
import { ReasonField } from './fields/ReasonField';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import * as z from 'zod';
import { ReportData } from '@/types/report';

export const formSchema = z.object({
  status: z.enum(['PENDING', 'APPROVED', 'REJECTED']),
  learning_status: z.string().min(1, 'กรุณาระบุสถานะการเรียนรู้'),
  submitted_on_time: z.boolean(),
  class_duration: z.number().optional(),
  teaching_duration: z.string().min(1, 'กรุณาระบุระยะเวลาการสอน'),
  teaching_type: z.string().optional(),
  reason: z.string().min(5, 'กรุณาระบุเหตุผลในการแก้ไข (อย่างน้อย 5 ตัวอักษร)'),
  rejection_reason: z.string().optional(),
});

interface EditReportFormProps {
  report: ReportData | null;
  onSubmit: (values: z.infer<typeof formSchema>) => Promise<void>;
  loading: boolean;
  onClose: () => void;
}

export const EditReportForm: React.FC<EditReportFormProps> = ({
  report,
  onSubmit,
  loading,
  onClose,
}) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      status: report?.status as 'PENDING' | 'APPROVED' | 'REJECTED' || 'PENDING',
      learning_status: report?.learning_status || '',
      submitted_on_time: report?.submitted_on_time || true,
      class_duration: report?.class_duration || undefined,
      teaching_duration: report?.teaching_duration || '',
      teaching_type: report?.teaching_type || '',
      reason: '',
      rejection_reason: report?.rejection_reason || '',
    },
  });

  React.useEffect(() => {
    if (report) {
      form.reset({
        status: report.status as 'PENDING' | 'APPROVED' | 'REJECTED',
        learning_status: report.learning_status || '',
        submitted_on_time: report.submitted_on_time !== false,
        class_duration: report.class_duration || undefined,
        teaching_duration: report.teaching_duration || '',
        teaching_type: report.teaching_type || '',
        reason: '',
        rejection_reason: report.rejection_reason || '',
      });
    }
  }, [report, form]);

  const showRejectionReason = form.watch('status') === 'REJECTED';

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <StatusField showRejectionReason={showRejectionReason} />
        <LearningStatusField />
        <SubmissionField />
        <TeachingInfoFields />
        <ReasonField />
        
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            ยกเลิก
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
};
